import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

// Import translations
import enCommon from './locales/en/common.json';
import frCommon from './locales/fr/common.json';
import enIntelligence from './locales/en/intelligence.json';
import frIntelligence from './locales/fr/intelligence.json';
import enEqmsForms from './locales/en/eqms-forms.json';
import frEqmsForms from './locales/fr/eqms-forms.json';
import enQms from './locales/en/qms.json';
import frQms from './locales/fr/qms.json';
import enCrm from './locales/en/crm.json';
import frCrm from './locales/fr/crm.json';
import enCustomerOnboarding from './locales/en/customer-onboarding.json';
import frCustomerOnboarding from './locales/fr/customer-onboarding.json';
import enChangelog from './locales/en/changelog.json';
import frChangelog from './locales/fr/changelog.json';
import enOperations from './locales/en/operations.json';
import frOperations from './locales/fr/operations.json';
import enOperationsFinancial from './locales/en/operations-financial.json';
import frOperationsFinancial from './locales/fr/operations-financial.json';
import enUsers from './locales/en/users.json';
import frUsers from './locales/fr/users.json';
import enRoles from './locales/en/roles.json';
import frRoles from './locales/fr/roles.json';
import enSites from './locales/en/sites.json';
import frSites from './locales/fr/sites.json';
import enLocations from './locales/en/locations.json';
import frLocations from './locales/fr/locations.json';
import enShippings from './locales/en/shippings.json';
import frShippings from './locales/fr/shippings.json';
import enQuotes from './locales/en/quotes.json';
import frQuotes from './locales/fr/quotes.json';
import enOrders from './locales/en/orders.json';
import frOrders from './locales/fr/orders.json';
import enSubscriptions from './locales/en/subscriptions.json';
import frSubscriptions from './locales/fr/subscriptions.json';
import enCalibrationProcedures from './locales/en/calibration-procedures.json';
import frCalibrationProcedures from './locales/fr/calibration-procedures.json';
import enStreams from './locales/en/streams.json';
import frStreams from './locales/fr/streams.json';
import enCarm from './locales/en/carm.json';
import frCarm from './locales/fr/carm.json';
import enInvoices from './locales/en/invoices.json';
import frInvoices from './locales/fr/invoices.json';
import enPlaneteCourrier from './locales/en/planete-courrier.json';
import frPlaneteCourrier from './locales/fr/planete-courrier.json';

import enCompanyPulse from './locales/en/company-pulse.json';
import frCompanyPulse from './locales/fr/company-pulse.json';

const resources = {
  en: {
    common: enCommon,
    'company-pulse': enCompanyPulse,
    intelligence: enIntelligence,
    qms: enQms,
    'eqms-forms': enEqmsForms,
    crm: enCrm,
    'customer-onboarding': enCustomerOnboarding,
    changelog: enChangelog,
    operations: enOperations,
    'operations-financial': enOperationsFinancial,
    users: enUsers,
    roles: enRoles,
    sites: enSites,
    locations: enLocations,
    shippings: enShippings,
    quotes: enQuotes,
    orders: enOrders,
    subscriptions: enSubscriptions,
    'calibration-procedures': enCalibrationProcedures,
    streams: enStreams,
    carm: enCarm,
    invoices: enInvoices,
    'planete-courrier': enPlaneteCourrier,
  },
  fr: {
    common: frCommon,
    'company-pulse': frCompanyPulse,
    intelligence: frIntelligence,
    qms: frQms,
    'eqms-forms': frEqmsForms,
    crm: frCrm,
    'customer-onboarding': frCustomerOnboarding,
    changelog: frChangelog,
    operations: frOperations,
    'operations-financial': frOperationsFinancial,
    users: frUsers,
    roles: frRoles,
    sites: frSites,
    locations: frLocations,
    shippings: frShippings,
    quotes: frQuotes,
    orders: frOrders,
    subscriptions: frSubscriptions,
    'calibration-procedures': frCalibrationProcedures,
    streams: frStreams,
    carm: frCarm,
    invoices: frInvoices,
    'planete-courrier': frPlaneteCourrier,
  },
};

type AppLanguage = 'en' | 'fr';

const LANGUAGE_KEY = 'atek-language';
const DEFAULT_LANGUAGE: AppLanguage = 'en';

function normalizeLanguage(value: string | null | undefined): AppLanguage | null {
  if (value === 'en' || value === 'fr') {
    return value;
  }
  return null;
}

function getSavedLanguage(): AppLanguage | null {
  return normalizeLanguage(localStorage.getItem(LANGUAGE_KEY));
}

function getInitialLanguage(): AppLanguage {
  return getSavedLanguage() ?? DEFAULT_LANGUAGE;
}

i18n
  .use(initReactI18next)
  .init({
    resources,
    lng: getInitialLanguage(),
    fallbackLng: 'en',
    ns: ['company-pulse', 'common', 'intelligence', 'qms', 'eqms-forms', 'crm', 'customer-onboarding', 'changelog', 'operations', 'operations-financial', 'users', 'roles', 'sites', 'locations', 'shippings', 'quotes', 'orders', 'subscriptions', 'calibration-procedures', 'streams', 'invoices', 'planete-courrier'],
    defaultNS: 'common',
    interpolation: {
      escapeValue: false, // React already escapes
    },
  });

export default i18n;

/**
 * Set language based on user session
 * Call this after session is loaded
 */
export function setLanguageFromSession(language: AppLanguage) {
  localStorage.setItem(LANGUAGE_KEY, language);
  void i18n.changeLanguage(language);
}

/**
 * Sync language from session without overriding explicit user preference.
 */
export function syncLanguageFromSession(language: AppLanguage) {
  const savedLanguage = getSavedLanguage();
  const nextLanguage = savedLanguage ?? language;

  if (!savedLanguage) {
    localStorage.setItem(LANGUAGE_KEY, nextLanguage);
  }

  if (i18n.language !== nextLanguage) {
    void i18n.changeLanguage(nextLanguage);
  }
}
